import { Platform, BrandAssets, FormCollectionFields } from '../types';

const baseUrl = 
  import.meta.env.VITE_ENV === 'production' 
    ? 'https://api.walky.me' 
    : import.meta.env.VITE_ENV === 'development' 
    ? 'https://walky.jvais.ovh' 
    : 'https://test.api.walky.me';

export const getQueryParam = (param: string): string | null => {
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);

  if (param === 'brandId') return segments[0] ?? null;
  if (param === 'userId') return segments[1] ?? null;

  return null;
};

export const getPlatform = async (): Promise<Platform> => {
  const userAgent = navigator.userAgent;

  const isApple = /iPhone|iPad|iPod|Macintosh/i.test(userAgent);
  const isGoogle = /Android/i.test(userAgent);

  if (isApple) {
    return 'apple';
  } else if (isGoogle) {
    return 'google';
  } else {
    return 'google';
  }
};

export const getBrandAssets = async (brandId: string): Promise<BrandAssets> => {
  const response = await fetch(`${baseUrl}/brands/${brandId}/assets`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch brand assets');
  }

  const { logoFile, bannerFile, title } = await response.json();

  return {
    logoUrl: `${baseUrl}/proxy/image?url=${logoFile}`,
    bannerUrl: bannerFile ? `${baseUrl}/proxy/image?url=${bannerFile}` : null,
    title,
  };
};

export const getFormCollectionFields = async (brandId: string): Promise<FormCollectionFields> => {
  const response = await fetch(`${baseUrl}/brands/${brandId}/collection-form/fields`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch form collection fields');
  }

  return response.json();
};

export const submitFormData = async (brandId: string, data: Record<string, any>): Promise<{ id: string }> => {
  const response = await fetch(`${baseUrl}/brands/${brandId}/collection-form/fields`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to submit form data');
  }

  return response.json();
};

export const getGoogleJwt = async (brandId: string, formCollectionId?: string): Promise<string> => {
  const url = `${baseUrl}/google/pass/${brandId}/get${formCollectionId ? `?collectionFormId=${formCollectionId}` : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error('Failed to fetch Google JWT');
  }

  const data = await response.json();
  return data.payload;
};

export const getApplePass = async (brandId: string, formCollectionId?: string): Promise<string> => {
  const url = `${baseUrl}/apple/pass/${brandId}/get${formCollectionId ? `?collectionFormId=${formCollectionId}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/vnd.apple.pkpass',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch Apple Pass');
  }

  const blob = await response.blob();
  return window.URL.createObjectURL(blob);
};

export const parseDate = (dateString: string): string => {
  const [day, month, year] = dateString.split('/');
  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10) - 1;
  const yearNum = parseInt(year, 10);

  const date = new Date(yearNum, monthNum, dayNum);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const formatDate = (date: Date): string => {
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

export const downloadFile = (url: string, filename: string): void => {
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.setAttribute('download', filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^(?:\+33|0)[1-9](?:[0-9]{8})$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};
